/**
 * Upload API - Handles video upload flow via S3 presigned URLs
 */
import { detectVideo } from '../utils/videoMime';
import { safePost, safeGet, buildApiUrl } from '../utils/safeApi';

/**
 * Detect file type for both videos and PDFs
 */
function detectFileType(file: File): { contentType: string | null; reason?: string } {
  const rawType = file?.type || '';
  
  // Check for PDF files
  if (rawType === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
    return { contentType: 'application/pdf' };
  }
  
  // Check for video files using existing detectVideo function
  const videoResult = detectVideo(file, file.name);
  if (videoResult.contentType) {
    return { contentType: videoResult.contentType };
  }
  
  // Unsupported file type
  return { 
    contentType: null, 
    reason: `Unsupported file type '${rawType}' and extension '${file.name.split('.').pop() || 'none'}'` 
  };
}

export interface PresignedUploadResponse {
  url: string;
  key: string;
  contentType: string;
  expiresIn: number;
  bucket: string;
}

/**
 * Request a presigned URL for direct S3 video upload (legacy lesson system)
 * Requires a real File object for proper MIME detection
 */
export async function presignVideoUpload(lessonId: number, file: File): Promise<PresignedUploadResponse> {
  if (!file || !(file instanceof File)) {
    throw new Error('no_file: A File object is required');
  }
  
  const { contentType: ct, reason } = detectVideo(file, file?.name);
  if (!ct) {
    throw new Error(`unsupported_type: ${reason || 'unknown'}`);
  }
  
  return safePost(buildApiUrl('/api/v1/uploads/presign-video'), {
    lessonId,
    fileName: file.name,
    contentType: ct
  });
}

/**
 * Request a presigned URL for content asset upload (new content system)
 * Automatically routes to appropriate S3 folder based on content type
 * Supports both video and PDF files
 */
export async function presignContentUpload(contentTypeId: number, file: File): Promise<PresignedUploadResponse> {
  if (!file || !(file instanceof File)) {
    throw new Error('no_file: A File object is required');
  }
  
  const { contentType: ct, reason } = detectFileType(file);
  if (!ct) {
    throw new Error(`unsupported_type: ${reason || 'unknown'}`);
  }
  
  return safePost(buildApiUrl('/api/v1/uploads/presign-video'), {
    contentTypeId,
    fileName: file.name,
    contentType: ct
  });
}

/**
 * Complete S3 video upload flow: presign -> upload to S3 -> create DB record
 * Requires a real File object for proper validation and upload
 */
export async function uploadVideoToS3(lessonId: number, file: File, duration?: number | null): Promise<any> {
  // Step 1: Get presigned URL (validates File and MIME type)
  const presigned = await presignVideoUpload(lessonId, file);
  
  // Step 2: Upload to S3
  const putResponse = await fetch(presigned.url, {
    method: 'PUT',
    headers: {
      'Content-Type': presigned.contentType || 'application/octet-stream'
    },
    body: file
  });
  
  if (!putResponse.ok) {
    throw new Error(`s3_upload_failed:${putResponse.status}`);
  }
  
  // Step 3: Create database record
  const contentType = presigned.contentType || '';
  const fileType = contentType.toLowerCase().includes('webm') ? 'webm' :
                   contentType.toLowerCase().includes('ogg') ? 'ogg' :
                   contentType.toLowerCase().includes('m4v') ? 'm4v' : 'mp4';
  
  return safePost(buildApiUrl(`/api/v1/lessons/${lessonId}/videos`), {
    s3_key: presigned.key,
    file_type: fileType,
    duration_sec: duration ?? null
  });
}

export interface SignedUrlResponse {
  signedUrl: string;
  fileType: 'youtube' | 's3';
}

/**
 * Get signed URL for content preview
 * For S3 videos, returns a signed URL. For YouTube videos, returns the original URL.
 */
export async function getContentSignedUrl(contentId: number): Promise<SignedUrlResponse> {
  const url = buildApiUrl(`/api/content/${contentId}/signed-url`);
  console.log('Making API call to:', url);
  try {
    const result = await safeGet(url);
    console.log('API call successful:', result);
    return result;
  } catch (error) {
    console.error('API call failed:', error);
    throw error;
  }
}
